import { zodResolver } from '@hookform/resolvers/zod';
import {
  GoToEddRequest,
  Mutation,
  SubmitEddResponseResultTypenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import {
  SourceName,
  useApplicationTrackErrorEvents,
} from 'features/application/hooks/useApplicationTrackErrorEvents';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import SubmitEdd from 'services/graphql/mutations/signup/SubmitEdd';
import EddQuestionsQuery from 'services/graphql/queries/EddQuestions';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { useMutation, useQuery } from 'urql';
import { z } from 'zod';

interface IWhereToGo {
  whereToGo: GoToEddRequest;
}

interface FormValues {
  [key: string]: boolean;
}

interface MutationInput {
  id: string;
  options: string[];
}

const getFullOptionId = (questionId: string, optionId: string) => {
  return `${questionId}_${optionId}`;
};

const EddQuestions = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'eddQuestions',
  });
  const [{ data, fetching }] = useQuery<IWhereToGo>({
    query: EddQuestionsQuery,
    requestPolicy: 'network-only',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: '/signup/edd-questions',
  });

  const [
    { data: submitData, fetching: submitting, error },
    submitEddQuestions,
  ] = useMutation<Mutation>(SubmitEdd);

  const insets = useSafeAreaInsets();
  const { logErrorEvent } = useApplicationTrackErrorEvents();
  const { handleLogout } = useContext<AuthContextProps>(AuthContext);
  const [snackbar, setSnackbar] = useState<{
    message: string;
  }>({
    message: '',
  });

  useAutoHideSnackbar(Boolean(snackbar.message), () => {
    setSnackbar({ message: '' });
  });

  useEffect(() => {
    if (error) {
      logErrorEvent(error.message, SourceName.EDD_QUESTIONS);
      setSnackbar({
        message: translatedText('errorMessage'),
      });
      return;
    }
    if (
      submitData?.submitEddResponse?.__typename ===
      SubmitEddResponseResultTypenames.SubmitEddResponseSuccess
    ) {
      router.replace('/');
    } else if (
      submitData?.submitEddResponse?.__typename ===
      SubmitEddResponseResultTypenames.SubmitEddResponseError
    ) {
      setSnackbar({
        message: submitData.submitEddResponse.message,
      });
    }
  }, [submitData, error, translatedText, logErrorEvent, router]);

  const questions = data?.whereToGo?.questions || [];

  const defaultValues = questions?.reduce((acc, question) => {
    question?.options?.forEach((option) => {
      acc[getFullOptionId(question.id, option.id)] = false;
    });
    return acc;
  }, {}) as FormValues;

  const schema = questions.reduce<Record<string, z.ZodBoolean>>(
    (acc, question) => {
      question?.options?.forEach((option) => {
        acc[getFullOptionId(question.id, option.id)] = z.boolean();
      }, {});
      return acc;
    },
    {},
  );

  const eddSchema = z.object(schema).refine((value) => {
    let hasAnsweredAllQuestions = true;
    questions.forEach((question) => {
      const hasAnsweredThisQuestion = Object.keys(value).some((key) => {
        const [questionId] = key.split('_');
        return question.id === questionId && value[key] === true;
      });
      if (!hasAnsweredThisQuestion) {
        hasAnsweredAllQuestions = false;
      }
    });
    return hasAnsweredAllQuestions;
  });

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(eddSchema),
    defaultValues,
  });

  const onFormSubmit = () => {
    const formValues = getValues();
    const mutationInputs: MutationInput[] = [];

    questions.forEach((question) => {
      const answeredOptions: string[] = [];
      Object.keys(formValues).forEach((key) => {
        const [questionId, optionId] = key.split('_');
        if (questionId === question.id && formValues[key] === true) {
          answeredOptions.push(optionId);
        }
      });
      mutationInputs.push({
        id: question.id,
        options: answeredOptions,
      });
    });

    void submitEddQuestions({
      eddResponseInput: mutationInputs,
    });
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <Stack.Screen
          options={{
            headerBackVisible: false,
            headerShown: false,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar {...props} />
            ),
          }}
        />
        <TitleMdText numberOfLines={0} style={styles.text}>
          {translatedText('title')}
        </TitleMdText>
        <BodyBaseText numberOfLines={0} style={styles.text}>
          {translatedText('subTitle')}
        </BodyBaseText>
        {fetching ? (
          <View style={styles.loaderStyle}>
            <LoadingSpinner />
          </View>
        ) : null}
        {!fetching
          ? questions?.map((question) => (
              <View key={question.id}>
                <TitleSmText numberOfLines={0} style={styles.text}>
                  {question.text}
                </TitleSmText>
                <BodyBaseText numberOfLines={0} style={styles.text}>
                  {translatedText('questionSubTitle')}
                </BodyBaseText>
                {question?.options?.map((option) => {
                  const optionId = getFullOptionId(question.id, option.id);
                  return (
                    <Controller
                      key={optionId}
                      control={control}
                      name={optionId}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Checkbox
                          testId={optionId}
                          checked={Boolean(value)}
                          onSelectedStateChange={() => {
                            const nextValue = !value;
                            onChange(nextValue);
                            onBlur();
                            reset({
                              ...getValues(),
                              [optionId]: nextValue,
                            });
                          }}
                          labelText={option.text} // This copy is coming from server so no translation
                          ariaLabel={option.text}
                        />
                      )}
                    />
                  );
                })}
              </View>
            ))
          : null}
      </ScrollView>
      <View style={styles.hairlineDivider}>
        <HairLineDivider />
      </View>
      <View style={[styles.buttonContainer, { marginBottom: insets.bottom }]}>
        <Button
          label={translatedText('buttons.next')}
          size="LARGE"
          type="PRIMARY"
          isLoading={fetching || submitting}
          onPress={onFormSubmit}
          analyticsId="nextButtonClicked"
          isDisabled={fetching || !isValid}
        />
        <Button
          label={translatedText('buttons.logOut')}
          size="LARGE"
          type="SECONDARY"
          onPress={handleLogout}
        />
      </View>
      {snackbar.message ? (
        <Snackbar duration={SNACKBAR_DURATION} text={snackbar.message} />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: { gap: theme.spacing.m, padding: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
  },
  hairlineDivider: {
    paddingBottom: theme.spacing.m,
  },
  loaderStyle: {
    alignSelf: 'center',
    display: 'flex',
    height: theme.spacing.l,
    marginTop: theme.spacing.l,
    width: theme.spacing.l,
  },
  scrollContainer: { paddingHorizontal: theme.spacing.m },
  text: {
    marginBottom: theme.spacing.s,
    marginTop: theme.spacing.m,
  },
});

export default EddQuestions;
